import React, { useState } from 'react';
import { Accordion, Card, Form, Button, Row, Col, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalculator } from '@fortawesome/free-solid-svg-icons';
import StatSelectionForm from './StatSelectionForm';
import { useForceUpdate } from '../Common/CustomHooks';
import RadioButton from '../Common/RadioButton';
import { columnManager } from '../Common/ColumnManager';
import { useEffect } from 'react';

function StatSelection(props) {
    const [origTimePeriod, setOrigTimePeriod] = useState('Platform Year');
    const [selectedForCombine, setSelectedForCombine] = useState([]);
    const [currentCalculatorColumn, setCurrentCalculatorColumn] = useState(null);
    const [updatedTimePeriod, setUpdatedTimePeriod] = useState('Platform Year');
    const [timePeriodManipulationType, setTimePeriodManipulationType] = useState('change');
    const [intersperseNewStats, setIntersperseNewStats] = useState(false);
    const [highlightedForDeletion, setHighlightedForDeletion] = useState([]);
    const forceUpdate = useForceUpdate();
    let sectionId = props.section === 'Stat' ? 'DisStat' : props.section === 'Criteria' ? 'NumCrit' : '';

    useEffect(() => {
        if (selectedForCombine.length > 1) {
            const statsToCombine = selectedForCombine.map(s => props.stats.find(stat => stat.index === s.statIndex));
            setCurrentCalculatorColumn({
                index: Math.max(...props.stats.map(s => s.index)) + 1,
                isCalculator: true,
                stat: null,
                value: '0',
                timePeriod: props.timePeriodNotSelectable ? 'Career To Date' : 'Platform Year',
                calculatorColumn: columnManager.combineColumns(statsToCombine, selectedForCombine.map(s => s.operator)),
                calculatorOperators: selectedForCombine.map(s => s.operator),
                calculatorStats: statsToCombine
            });
        } else {
            setCurrentCalculatorColumn(null);
        }
    }, [selectedForCombine]);

    async function setStat(statIndex, field, value) {
        let tempStats = props.stats.slice(0);
        let tempStatIndex = tempStats.findIndex(ts => ts.index === statIndex);
        tempStats[tempStatIndex][field] = value;
        if (field === 'stat') {
            let column = await columnManager.getSingleColumnById(value);
            tempStats[tempStatIndex].column = column;
            tempStats[tempStatIndex].timePeriod =
                props.timePeriodNotSelectable
                    ? 'Career To Date'
                    : column.formInputs.defaultTimePeriod;

            if (column.formInputs.inputType === 'Date') {
                tempStats[tempStatIndex].value = new Date();
            }
            if (['Select', 'MultiSelect'].includes(column.formInputs.inputType)) {
                tempStats[tempStatIndex].operator = '=';
                tempStats[tempStatIndex].value = column.formInputs.selectOptions[0]?.value ?? '0';
            }
        }
        props.setStats(tempStats);
    }

    function removeStat(statIndex) {
        let iswFilterMap = new Map();
        let igwFilterMap = new Map();
        for (let i = statIndex + 1; i < props.stats.length; i++) {
            iswFilterMap.set(i - 1, document.getElementById(`${sectionId}-ISW-dropZone-${i}`)?.children ?? [])
            igwFilterMap.set(i - 1, document.getElementById(`${sectionId}-IGW-dropZone-${i}`)?.children ?? [])
        }

        let statToRemove = props.stats.find(s => s.index === statIndex);
        let dependentCalculatorStats = props.stats
            .filter(s => s.calculatorStats?.findIndex(cs => cs.index === statToRemove.index) > -1);
        if (dependentCalculatorStats.length > 0) {
            // Get names of dependent stats for the confirmation message
            const dependentStatNames = dependentCalculatorStats
                .map(s => `"${s.calculatorColumn.getCalculatorFormula()}"`)
                .join(', ');

            // Highlight the stat to be removed and its dependents
            const allToHighlight = [statToRemove.index, ...dependentCalculatorStats.map(s => s.index)];
            setHighlightedForDeletion(allToHighlight);

            const confirmation = window.confirm(
                `You must first remove the following calculated stat(s): ${dependentStatNames}.`
            );
            
            // Always clear highlighting after the action
            setTimeout(() => setHighlightedForDeletion([]), 3000);
        } else {
            let tempStats = props.stats.filter(s => statToRemove !== s);
            props.setStats(tempStats, () => {
                for (const [key, value] of iswFilterMap) {
                    for (let i = 0; i < value.length; i++) {
                        document.getElementById(`${sectionId}-ISW-dropZone-${key}`)?.appendChild(value[i]);
                    }
                }
                for (const [key, value] of igwFilterMap) {
                    for (let i = 0; i < value.length; i++) {
                        document.getElementById(`${sectionId}-IGW-dropZone-${key}`)?.appendChild(value[i]);
                    }
                }
                forceUpdate();
            });
        }
    }

    function addNewStat() {
        let tempStats = props.stats.slice(0);
        tempStats.push({
            index: props.stats.length > 0 ? Math.max(...props.stats.map(s => s.index)) + 1 : 0,
            stat: null,
            operator: '>',
            value: '0',
            timePeriod: props.timePeriodNotSelectable ? 'Career To Date' : 'Platform Year',
        });
        props.setStats(tempStats);
    }

    function replaceClassAndIdIndexOnFilterAndChildren(curFilter, curIndex, newIndex) {
        curFilter.id = curFilter.id?.replace(/dropzone-\d+/, `dropZone-${newIndex}`);
        curFilter.id = curFilter.id?.replace(/criteria-\d+/, `criteria-${newIndex}`);
        curFilter.className = curFilter.className?.replace(/dropzone-\d+/, `dropZone-${newIndex}`);
        curFilter.className = curFilter.className?.replace(/criteria-\d+/, `criteria-${newIndex}`);
        for (let i = 0; i < curFilter.childNodes.length; i++) {
            replaceClassAndIdIndexOnFilterAndChildren(curFilter.childNodes[i], curIndex, newIndex);
        }
    }

    function appendAllFiltersToEle(filters, ele, curIndex, newIndex) {
        let docFrag = document.createDocumentFragment();
        filters.forEach(node => {
            replaceClassAndIdIndexOnFilterAndChildren(node, curIndex, newIndex);
        })
        docFrag.append(...filters);
        ele?.appendChild(docFrag);
    }

    function reorderStat(statIndex, direction) {
        let newIndex = statIndex + (direction === 'up' ? -1 : 1);
        let curIGWId = `DisStat-IGW-dropZone-${statIndex}`;
        let newIGWId = `DisStat-IGW-dropZone-${newIndex}`;
        let curIGWFilters = document.getElementById(curIGWId)?.childNodes ?? [];
        let newIGWFilters = document.getElementById(newIGWId)?.childNodes ?? [];
        let curISWId = `DisStat-ISW-dropZone-${statIndex}`;
        let newISWId = `DisStat-ISW-dropZone-${newIndex}`;
        let curISWFilters = document.getElementById(curISWId)?.childNodes ?? [];
        let newISWFilters = document.getElementById(newISWId)?.childNodes ?? [];

        let offset = direction === 'up' ? -1 : 2;
        let newStatIndex = statIndex + offset;
        let tempStats = props.stats.slice(0);
        let statToMove = tempStats.slice(statIndex, statIndex + 1)[0];
        statToMove.iswFilter = null;
        statToMove.igwFilter = null;
        tempStats.splice(newStatIndex, 0, statToMove);
        tempStats.splice(statIndex + (direction === 'up' ? 1 : 0), 1);
        props.setStats(tempStats, () => {
            appendAllFiltersToEle(curIGWFilters, document.getElementById(newIGWId), statIndex, newIndex);
            appendAllFiltersToEle(newIGWFilters, document.getElementById(curIGWId), statIndex, newIndex);
            appendAllFiltersToEle(curISWFilters, document.getElementById(newISWId), statIndex, newIndex);
            appendAllFiltersToEle(newISWFilters, document.getElementById(curISWId), statIndex, newIndex);
            forceUpdate();
        });
    }

    function getTimePeriodsFromFormInputs(formInputs) {
        let retTimePeriods = [];
        if ((props.section === 'Criteria' && formInputs.criteriaSingleYearTimePeriods) ||
            (props.section === 'Stat' && formInputs.disStatSingleYearTimePeriods)) {
            retTimePeriods.push(...singleYearTimePeriods);
        }
        if ((props.section === 'Criteria' && formInputs.criteriaAggTimePeriods) ||
            (props.section === 'Stat' && formInputs.disStatAggTimePeriods)) {
            retTimePeriods.push(...aggTimePeriods);
        }
        return retTimePeriods;
    }

    function updateStatsTimePeriods() {
        let tempStats = props.stats.slice(0);

        tempStats.forEach(curTempStat => {
            if (curTempStat.timePeriod === origTimePeriod
                && getTimePeriodsFromFormInputs(curTempStat.column?.formInputs).includes(updatedTimePeriod)) {
                curTempStat.timePeriod = updatedTimePeriod;
            }
        })

        props.setStats(tempStats);
    }

    function duplicateStatsAsTimePeriod() {
        let tempStats = props.stats.slice(0);

        let newStats = [];
        let dupeStats = [];
        tempStats.forEach((curTempStat, loopIndex) => {
            curTempStat.statWasCopied = null;
            curTempStat.origIndexIGWFilters = null;
            curTempStat.origIndexISWFilters = null;
            curTempStat.origIndex = loopIndex;
            curTempStat.index = loopIndex + (intersperseNewStats ? dupeStats.length : 0);
            newStats.push(curTempStat);
            if (curTempStat.timePeriod === origTimePeriod
                && getTimePeriodsFromFormInputs(curTempStat.column?.formInputs)
                    .includes(updatedTimePeriod)) {
                let dupeTempStat = Object.assign({}, curTempStat);
                curTempStat.statWasCopied = true;
                curTempStat.origIndexIGWFilters = document
                    .getElementById(`DisStat-IGW-dropZone-${loopIndex}`)?.childNodes ?? [];
                curTempStat.origIndexISWFilters = document
                    .getElementById(`DisStat-ISW-dropZone-${loopIndex}`)?.childNodes ?? [];
                dupeTempStat.timePeriod = updatedTimePeriod;
                if (intersperseNewStats) {
                    dupeTempStat.index = loopIndex + dupeStats.length + 1;
                    newStats.push(dupeTempStat);
                } else {
                    dupeTempStat.index = tempStats.length + dupeStats.length;
                }
                dupeTempStat.origIndex = loopIndex;
                dupeStats.push(dupeTempStat);
            }
        })

        if (!intersperseNewStats) {
            newStats = newStats.concat(dupeStats);
        }
        props.setStats(newStats, () => {
            newStats.forEach(newStat => {
                if (newStat.statWasCopied) {
                    appendAllFiltersToEle(
                        newStat.origIndexIGWFilters,
                        document.getElementById(`DisStat-IGW-dropZone-${newStat.index}`),
                        newStat.origIndex,
                        newStat.index
                    );
                    appendAllFiltersToEle(
                        newStat.origIndexISWFilters,
                        document.getElementById(`DisStat-ISW-dropZone-${newStat.index}`),
                        newStat.origIndex,
                        newStat.index
                    );
                }
            })
            forceUpdate();
        });
    }

    let displayTimePeriod = !props.timePeriodNotSelectable
        || (props.section === 'Criteria' && ['throughX', 'dateRange'].includes(props.tabName));

    return (
        <React.Fragment>
            {
                props.stats.length > 0 && !props.timePeriodNotSelectable &&
                <Accordion>
                    <Card>
                        <Card.Header>
                            <Accordion.Toggle as={Button} variant='link' eventKey='0'>Change/Add Time Periods for Multiple Stats</Accordion.Toggle>
                        </Card.Header>
                        <Accordion.Collapse eventKey='0'>
                            <Card.Body>
                                <Row>
                                    <Col
                                        xs={12}
                                        md={{ span: 6, offset: 3 }}
                                        lg={{ span: 4, offset: 4 }}
                                        style={{ border: 'solid 1px black' }}
                                    >
                                        <Row>
                                            <Col>
                                                <RadioButton
                                                    checked={timePeriodManipulationType === 'change'}
                                                    key='1'
                                                    name='timePeriodManipulationType'
                                                    id='change'
                                                    value='change'
                                                    label='Change Existing Stats'
                                                    onClick={(event) => setTimePeriodManipulationType(event.target.value)}
                                                />
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col>
                                                <RadioButton
                                                    checked={timePeriodManipulationType === 'duplicate'}
                                                    key='2'
                                                    name='timePeriodManipulationType'
                                                    id='duplicate'
                                                    value='duplicate'
                                                    label='Duplicate Existing Stats'
                                                    onClick={(event) => setTimePeriodManipulationType(event.target.value)}
                                                />
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={12} lg={{ span: 5, offset: 1 }} style={{ display: 'flex', alignItems: 'center' }}>
                                        <span style={{ marginRight: '5px' }}>
                                            {timePeriodManipulationType === 'change' ? 'Change:' : 'Duplicate:'}
                                        </span>
                                        <Form.Control as='select' onChange={(event) => setOrigTimePeriod(event.target.value)}>
                                        {
                                            singleYearTimePeriods
                                                .concat(aggTimePeriods)
                                                .filter(timePeriod => props.stats.findIndex(stat => stat.timePeriod === timePeriod) !== -1)
                                                .map((timePeriod) => {
                                                    return (
                                                        <option
                                                            selected={origTimePeriod === timePeriod ? 'selected' : false}
                                                            key={timePeriod}
                                                            value={timePeriod}
                                                        >
                                                            {timePeriod}
                                                        </option>
                                                    )
                                                })
                                        }
                                        </Form.Control>
                                    </Col>
                                    <Col xs={12} lg={5} style={{ display: 'flex', alignItems: 'center' }}>
                                        <span style={{ marginRight: '5px' }}>
                                            {timePeriodManipulationType === 'change' ? 'To:' : 'As:'}
                                        </span>
                                        <Form.Control as='select' onChange={(event) => setUpdatedTimePeriod(event.target.value)}>
                                            {
                                                singleYearTimePeriods
                                                    .concat(aggTimePeriods)
                                                    .map((timePeriod, mapIndex) => {
                                                        return (
                                                            <option
                                                                selected={updatedTimePeriod === timePeriod ? 'selected' : false}
                                                                key={mapIndex}
                                                                value={timePeriod}
                                                            >
                                                                {timePeriod}
                                                            </option>
                                                        )
                                                    })
                                            }
                                        </Form.Control>
                                    </Col>
                                </Row>
                                {
                                    timePeriodManipulationType !== 'change' &&
                                    <Row>
                                        <Col
                                            xs={12}
                                            md={{ span: 6, offset: 3 }}
                                            lg={{ span: 4, offset: 4 }}
                                            style={{ border: 'solid 1px black' }}
                                        >
                                            <Row>
                                                <Col>
                                                    <RadioButton
                                                        checked={intersperseNewStats}
                                                        disabled={timePeriodManipulationType === 'change'}
                                                        key='1'
                                                        name='intersperse'
                                                        label='Intersperse Duplicate Stats'
                                                        onClick={() => setIntersperseNewStats(true)}
                                                    />
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col>
                                                    <RadioButton
                                                        checked={!intersperseNewStats}
                                                        disabled={timePeriodManipulationType === 'change'}
                                                        key='2'
                                                        name='intersperse'
                                                        label='Show Duplicate Stats at End'
                                                        onClick={() => setIntersperseNewStats(false)}
                                                    />
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                }
                                <Row>
                                    <Col>
                                        {
                                            timePeriodManipulationType === 'change' &&
                                            <Button
                                                variant='info'
                                                onClick={() => updateStatsTimePeriods()}
                                            >
                                                Apply Change
                                            </Button>
                                        }
                                        {
                                            timePeriodManipulationType !== 'change' &&
                                            <Button
                                                variant='info'
                                                onClick={() => duplicateStatsAsTimePeriod()}
                                            >
                                                Apply Change
                                            </Button>
                                        }
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Accordion.Collapse>
                    </Card>
                </Accordion>
            }
            <Row key='0'>
                <Col lg={3}>
                    Stat
                </Col>
                {
                    ['Player Criteria', 'Criteria'].includes(props.section) &&
                    <React.Fragment>
                        <Col lg={2}>
                            Operator
                        </Col>
                        <Col lg={2}>
                            &nbsp;&nbsp;Value
                        </Col>
                    </React.Fragment>
                }
                {
                    displayTimePeriod &&
                    <Col lg={2}>
                        &nbsp;&nbsp;Time Period
                    </Col>
                }
                <Col lg={1}></Col>
            </Row>
            {
                props.stats.map((curStat, index) => (
                    <Row key={curStat.index}>
                        <Col>
                            <StatSelectionForm
                                selectableStatsByGroup={props.selectableStatsByGroup}
                                colGroups={props.colGroups}
                                section={props.section}
                                tabName={props.tabName}
                                timePeriodNotSelectable={props.timePeriodNotSelectable}
                                hideFilters={props.hideFilters}
                                stats={props.stats}
                                curStat={curStat}
                                index={index}
                                setStat={setStat}
                                removeStat={removeStat}
                                reorderStat={reorderStat}
                                getTimePeriodsFromFormInputs={getTimePeriodsFromFormInputs}
                                isHighlighted={highlightedForDeletion.includes(curStat.index)}
                            >
                                {
                                    props.useCalculator && (curStat.column?.formatter?.name === 'genericFormatter' || curStat.isCalculator) &&
                                    <>
                                        <Col xs='auto'>
                                            <Form.Check
                                                type='checkbox'
                                                checked={selectedForCombine.findIndex(s => s.statIndex === curStat.index) > -1}
                                                onChange={e => {
                                                    if (e.target.checked) {
                                                        setSelectedForCombine([...selectedForCombine, {statIndex: curStat.index, operator: '+'}]);
                                                    } else {
                                                        let statIndex = selectedForCombine.findIndex(s => s.statIndex === curStat.index);
                                                        setSelectedForCombine(selectedForCombine.toSpliced(statIndex, 1));
                                                    }
                                                }}
                                                label='Combine'
                                            />
                                        </Col>
                                        {
                                            selectedForCombine.findIndex(s => s.statIndex === curStat.index) > -1 &&
                                            <Col xs='auto'>
                                                {selectedForCombine.findIndex(s => s.statIndex === curStat.index) + 1}
                                            </Col>
                                        }
                                        {
                                            selectedForCombine.findIndex(s => s.statIndex === curStat.index) > 0 &&
                                            <Col xs='auto'>
                                                <Form.Control
                                                    as='select'
                                                    value={selectedForCombine.find(s => s.statIndex === curStat.index).operator}
                                                    onChange={e => {
                                                        let statIndex = selectedForCombine.findIndex(s => s.statIndex === curStat.index);
                                                        setSelectedForCombine(selectedForCombine.toSpliced(
                                                            statIndex,
                                                            1,
                                                            Object.assign({}, selectedForCombine[statIndex], {operator: e.target.value })
                                                        ))}
                                                    }
                                                    style={{ width: 100 }}
                                                >
                                                    <option value='+'>Sum (+)</option>
                                                    <option value='-'>Subtract (-)</option>
                                                    <option value='*'>Multiply (*)</option>
                                                    <option value='/'>Divide (/)</option>
                                                </Form.Control>
                                            </Col>
                                        }
                                    </>
                                }
                                
                            </StatSelectionForm>
                        </Col>
                    </Row>
                )
            )}
            <Row>
                <Col lg={3}>
                    <Button variant='info' onClick={() => addNewStat()}>
                        <img width='14' height='13' alt='' src={'./images/Filter.png'} type='button' />
                        Add {props.section}
                    </Button>
                </Col>
                {
                    props.section === 'Stat' && props.useCalculator &&
                    <>
                        <Col />
                        <Col xs='auto'>
                            <Button
                                variant='info'
                                disabled={selectedForCombine.length < 2}
                                onClick={() => {
                                    props.setStats([...props.stats, currentCalculatorColumn]);
                                    setSelectedForCombine([]);
                                    setCurrentCalculatorColumn(null);
                                }}
                            >
                                <FontAwesomeIcon icon={faCalculator} style={{ marginRight: '5px' }} />
                                Create Calculated Column
                            </Button>
                        </Col>
                        <Col xs={12} style={{ textAlign: 'right'}}>
                            {currentCalculatorColumn?.calculatorColumn?.getCalculatorFormula()}
                        </Col>
                    </>
                }
            </Row>
        </React.Fragment>
    )
}

export default StatSelection;

let singleYearTimePeriods = [
    'Platform Year',
    'Platform Year - 1',
    'Platform Year - 2',
    'Platform Year - 3',
    'Platform Year - 4',
    'Platform Year + 1',
    'Platform Year + 2',
    'Platform Year + 3',
    'Platform Year + 4',
]

let aggTimePeriods = [
    'Career To Date',
    'Career Thru PY-1',
    'Last 2 Years',
    'Last 3 Years',
    'Last 4 Years',
    'Next 2 Years',
    'Next 3 Years',
    'Next 4 Years',
]
